import { useConfig } from '@/hooks/useConfig';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Environment from './components/Environment/Environment';
import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Layout from './Layout';
import './locales/i18n';
import { setLanguageFromQueryParam } from './locales/i18n';
import RouterProvider from './router/RouterProvider';
import { akovaTheme } from './theme/theme';

const DebugPanel = import.meta.env.DEV ? lazy(() => import('@/debug/DebugPanel')) : () => null;

const App = () => {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  const { config } = useConfig();

  useEffect(() => {
    setLanguageFromQueryParam(lang);
  }, [lang]);

  useEffect(() => {
    if (!window.__SENTRY_INITIALIZED__) {
      Sentry.init({
        dsn: 'https://1a5282b534df3597e77e7f6747f3b41f@o4507222507520000.ingest.de.sentry.io/4508652354404432',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: true
          })
        ],
        enabled: import.meta.env.MODE !== 'development',
        environment: config.environment,
        debug: false,
        // Performance Monitoring
        tracesSampleRate: 0.5,
        // Session and Error Replay
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.25
      });
      window.__SENTRY_INITIALIZED__ = true;
    }
  }, []);

  return (
    <Sentry.ErrorBoundary>
      <ThemeProvider theme={akovaTheme()}>
        <ScrollToTop />
        <CssBaseline />
        <Header />
        <Environment />
        <Layout>
          <RouterProvider />
        </Layout>
        <Footer />
        <Suspense fallback={null}>
          <DebugPanel />
        </Suspense>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
